import React, { useEffect, useMemo, useState } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { MdOutlinePending } from 'react-icons/md'
import { RiMailSendLine } from 'react-icons/ri'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Modal, Table } from 'components/Ui'
import { Success } from 'utils/alerts'
import { numberFormat } from 'components/helper'
import dayjs from 'dayjs'
import { ApproveRejectRecordApi } from 'services/RateCardService'
import { Field, Form, Formik } from 'formik'
import { Button, SelectCreatable, TextArea } from 'components/Form'
import * as Yup from 'yup'
import { sendApprovedRejectMailApi } from 'services/InvoiceService'
import { getApiUser } from 'services/TransportProviderService'
injectReducer('rejectedInvoices', reducer)

const validationSchema = Yup.object().shape({
    email: Yup.array().required('Please select user or enter email'),
    note: Yup.string().nullable(),
})

export default function RejectedInvoices() {
    const dispatch = useDispatch()
    const { data, tableData, loading } = useSelector(
        (state) => state.rejectedInvoices.data,
    )
    const [invoice, setInvoice] = useState('')
    const [open, setOpen] = useState(false)
    const [vendorData, setVendorData] = useState([])

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        tableData.service,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const getUserData = (datas) => {
        const data = {
            transport_provider: datas?.transport_provider,
        }
        if (datas?.company) {
            data.company = datas?.company
        }
        getApiUser(data)
            .then((response) => {
                let arr = [
                    ...response?.data?.transport_admin,
                    ...response?.data?.transport_user,
                ]
                setVendorData(
                    arr.map((item) => {
                        item.label = `${item?.title}. ${item?.user?.first_name} ${item.user.last_name}`
                        item.value = item.user.email
                        return item
                    }),
                )
            })
            .catch((error) => error)
    }

    const handleStatus = (id, data) => {
        ApproveRejectRecordApi(id, data)
            .then(() => {
                Success(
                    `Invoice moved to ${data.status} Invoices Successfully!`,
                )
                fetchData()
            })
            .catch((error) => error)
    }

    const handleSubmit = (values, { setSubmitting }) => {
        setSubmitting(true)
        const mails = values?.email?.join(',')
        sendApprovedRejectMailApi({
            invoice_num: invoice,
            ...values,
            mail_ids: mails,
        })
            .then(() => {
                setSubmitting(false)
                setOpen(false)
                Success('Email Sent Successfully!')
            })
            .catch((error) => {
                setSubmitting(false)
                setOpen(false)
                return error
            })
    }

    const columns = useMemo(
        () => [
            {
                field: 'invoice',
                header: 'Invoice',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_id?.invoice.split('/')[
                        row?.invoice_id?.invoice.split('/').length - 1
                    ],
            },
            {
                field: 'invoice_num',
                header: 'Invoice Number',
                className: 'min-w-[150px]',
            },
            {
                field: 'invoiced_total',
                header: 'Invoice Total',
                className: 'min-w-[150px]',
                renderCell: (row) => numberFormat(row?.invoiced_total, 'AUD'),
            },
            {
                field: 'calculated_total',
                header: 'Calculated Total',
                className: 'min-w-[150px]',
                renderCell: (row) => numberFormat(row?.calculated_total, 'AUD'),
            },
            {
                field: 'total_difference',
                header: 'Total difference',
                className: 'min-w-[150px]',
                renderCell: (row) => numberFormat(row?.total_difference, 'AUD'),
            },
            {
                field: 'invoice_date',
                header: 'Invoice Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_date
                        ? dayjs(row?.invoice_date).format('DD-MMM-YYYY')
                        : '-',
            },
            {
                field: 'uploaded_at',
                header: 'Uploaded At',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_id?.uploaded_at
                        ? dayjs(row?.invoice_id?.uploaded_at).format(
                              'DD-MMM-YYYY',
                          )
                        : '-',
            },
            {
                field: 'status',
                header: 'Status',
                className: 'min-w-[150px]',
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            <Tooltip title={'Send Mail'} placement={'top'}>
                                <span
                                    className={`cursor-pointer hover:text-primary`}
                                    onClick={() => {
                                        getUserData({
                                            transport_provider:
                                                row?.assignment
                                                    ?.transport_provider,
                                            company:
                                                row?.assignment?.company_id,
                                        })
                                        setInvoice(row?.invoice_num)
                                        setOpen(true)
                                    }}
                                >
                                    <RiMailSendLine size={20} />
                                </span>
                            </Tooltip>
                            <Tooltip title={'Pending'} placement={'top'}>
                                <ConfirmationModal
                                    message={
                                        'Are you sure you want to move this invoice into pending invoices!'
                                    }
                                    onConfirm={() => {
                                        handleStatus(row.id, {
                                            status: 'Pending',
                                            invoice_num: row.invoice_num,
                                        })
                                    }}
                                >
                                    <span
                                        className={`cursor-pointer hover:text-primary`}
                                    >
                                        <MdOutlinePending size={20} />
                                    </span>
                                </ConfirmationModal>
                            </Tooltip>
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            tableData.service,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Rejected Invoices
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                // add_new={checkPermission('extra-charges', 'create') ? '/master-data/extra-charges/create' : ''}
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
            <Modal
                show={open}
                onClose={() => {
                    setOpen(false)
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">
                        {'Send Email'}
                    </h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <Formik
                                initialValues={{
                                    email: '',
                                    status: 'Rejected',
                                    newMail: '',
                                    note: '',
                                }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                                            <Field name="email">
                                                {({ field, form }) => (
                                                    <SelectCreatable
                                                        id="email"
                                                        name="email"
                                                        label="Note"
                                                        placeholder="Note"
                                                        value={(
                                                            vendorData ?? []
                                                        ).filter((e) =>
                                                            (
                                                                values?.email ??
                                                                []
                                                            ).includes(e.value),
                                                        )}
                                                        options={
                                                            vendorData ?? []
                                                        }
                                                        onChange={(e) => {
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.map(
                                                                    (f) =>
                                                                        f.value,
                                                                ),
                                                            )
                                                        }}
                                                        onInputChange={(
                                                            newValue,
                                                            actionMeta,
                                                        ) => {
                                                            form.setFieldValue(
                                                                'newMail',
                                                                newValue,
                                                            )
                                                            if (
                                                                actionMeta.action ===
                                                                    'set-value' &&
                                                                values.newMail
                                                            ) {
                                                                let arr = [
                                                                    ...vendorData,
                                                                ]
                                                                arr.push({
                                                                    label: values.newMail,
                                                                    value: values.newMail,
                                                                })
                                                                setVendorData(
                                                                    arr,
                                                                )
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    values.newMail,
                                                                )
                                                            }
                                                        }}
                                                        error={
                                                            errors.email &&
                                                            touched.email
                                                        }
                                                        message={errors.email}
                                                        isMulti
                                                    />
                                                )}
                                            </Field>
                                            <Field name="note">
                                                {({ field, form }) => (
                                                    <TextArea
                                                        label="Note"
                                                        name="note"
                                                        error={
                                                            errors.note &&
                                                            touched.note
                                                        }
                                                        message={errors.note}
                                                        value={values?.note}
                                                        onChange={(e) =>
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-[#F05540] border border-[#F05540] dark:text-[#F05540] dark:border-[#F05540]"
                                                onClick={() => setOpen(false)}
                                                type="button"
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
